import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { format } from "date-fns";
import openSocket from "../../services/socket-io";
import useSound from "use-sound";

import Popover from "@mui/material/Popover";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import { makeStyles } from "@mui/styles";
import Badge from "@mui/material/Badge";
import Typography from "@mui/material/Typography";
import NotificationsIcon from "@mui/icons-material/Notifications";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningOutlinedIcon from "@mui/icons-material/WarningOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { i18n } from "../../translate/i18n";
import alertSound from "../../assets/sound.mp3";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: 350,
		maxWidth: "calc(100vw - 32px)",
		[theme.breakpoints.down("sm")]: {
			width: 270,
		},
	},
	noShadow: {
		boxShadow: "none !important",
	},
	iconButton: {
		color: theme.palette.text.primary,
	},
	alertItem: {
		borderBottom: `1px solid ${theme.palette.divider}`,
		alignItems: "flex-start",
	},
	alertTime: {
		fontSize: 11,
		color: theme.palette.text.secondary,
		marginTop: 2,
	},
	severityInfo: { color: theme.palette.info?.main || "#2196f3" },
	severityWarning: { color: theme.palette.warning?.main || "#ff9800" },
	severityError: { color: theme.palette.error?.main || "#f44336" },
}));

const SeverityIcon = ({ severity, className }) => {
	if (severity === "warning") return <WarningOutlinedIcon className={className} />;
	if (severity === "error") return <ErrorOutlineIcon className={className} />;
	return <InfoOutlinedIcon className={className} />;
};

const NotificationsPopOver = () => {
	const classes = useStyles();

	const navigate = useNavigate();
	const location = useLocation();
	const { user } = useContext(AuthContext);
	const ticketIdUrl = +location.pathname.split("/")[2];
	const ticketIdRef = useRef(ticketIdUrl);
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [systemAlerts, setSystemAlerts] = useState([]);

	const [, setDesktopNotifications] = useState([]);

	const [play] = useSound(alertSound);
	const soundAlertRef = useRef();

	const navigateRef = useRef(navigate);

	useEffect(() => {
		soundAlertRef.current = play;

		if (!("Notification" in window)) {
			console.log("This browser doesn't support notifications");
		} else if (Notification.permission === "default") {
			document.addEventListener("click", () => Notification.requestPermission(), { once: true });
		}
	}, [play]);

	useEffect(() => {
		ticketIdRef.current = ticketIdUrl;
	}, [ticketIdUrl]);

	useEffect(() => {
		const socket = openSocket();

		socket.on("connect", () => {
			socket.emit("joinNotification");
			if (user?.id) socket.emit("joinTickets", `user-${user.id}`);
		});

		// Mantém som + notificação desktop para novas mensagens (sem adicionar ao sino)
		socket.on("appMessage", data => {
			if (
				data.action === "create" &&
				!data.message.read &&
				(data.ticket.userId === user?.id || !data.ticket.userId)
			) {
				const shouldNotNotificate =
					(data.message.ticketId === ticketIdRef.current &&
						document.visibilityState === "visible") ||
					(data.ticket.userId && data.ticket.userId !== user?.id) ||
					data.ticket.isGroup;

				if (shouldNotNotificate) return;

				handleMessageNotification(data);
			}
		});

		// Notificação de transferência: ticket foi atribuído a este agente
		socket.on("ticket", data => {
			if (
				data.action === "update" &&
				data.ticket?.userId === user?.id &&
				data.ticket?.status === "open"
			) {
				const contactName = data.ticket?.contact?.name || "contato";
				setSystemAlerts(prev => [{
					id: Date.now(),
					severity: "info",
					title: "Ticket transferido para você",
					body: `Atendimento com ${contactName} foi atribuído a você.`,
					timestamp: new Date().toISOString(),
					ticketId: data.ticket.id,
				}, ...prev]);
				soundAlertRef.current();
			}
		});

		// Alertas do sistema ficam no sino
		socket.on("systemAlert", alert => {
			setSystemAlerts(prev => [{ ...alert, id: Date.now() }, ...prev]);
			soundAlertRef.current();
		});

		return () => {
			socket.disconnect();
		};
	}, [user]);

	const handleMessageNotification = data => {
		const { message, contact, ticket } = data;

		const options = {
			// LGPD/SEC-004: não expor o conteúdo da mensagem na notificação do SO.
			body: `Nova mensagem recebida - ${format(new Date(), "HH:mm")}`,
			icon: contact.profilePicUrl,
			tag: ticket.id,
			renotify: true,
		};

		const notification = new Notification(
			`${i18n.t("tickets.notification.message")} ${contact.name}`,
			options
		);

		notification.onclick = e => {
			e.preventDefault();
			window.focus();
			navigateRef.current(`/tickets/${ticket.id}`);
		};

		setDesktopNotifications(prevState => {
			const idx = prevState.findIndex(n => n.tag === notification.tag);
			if (idx !== -1) {
				prevState[idx] = notification;
				return [...prevState];
			}
			return [notification, ...prevState];
		});

		soundAlertRef.current();
	};

	const handleClick = () => {
		setIsOpen(prevState => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const severityClass = severity => {
		if (severity === "warning") return classes.severityWarning;
		if (severity === "error") return classes.severityError;
		return classes.severityInfo;
	};

	return (
		<>
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				className={classes.iconButton}
			>
				<Badge badgeContent={systemAlerts.length} color="secondary">
					<NotificationsIcon />
				</Badge>
			</IconButton>
			<Popover
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				classes={{ paper: classes.popoverPaper }}
				onClose={handleClickAway}
			>
				<List dense className={classes.tabContainer}>
					{systemAlerts.length === 0 ? (
						<ListItem>
							<ListItemText>Nenhum alerta do sistema</ListItemText>
						</ListItem>
					) : (
						systemAlerts.map(alert => (
							<ListItem key={alert.id} className={classes.alertItem} button onClick={() => {
								setSystemAlerts(prev => prev.filter(a => a.id !== alert.id));
								if (alert.ticketId) navigateRef.current(`/tickets/${alert.ticketId}`);
							}}>
								<ListItemIcon style={{ minWidth: 36 }}>
									<SeverityIcon severity={alert.severity} className={severityClass(alert.severity)} />
								</ListItemIcon>
								<ListItemText
									primary={<Typography variant="body2"><strong>{alert.title}</strong></Typography>}
									secondary={
										<>
											<Typography variant="body2" component="span">{alert.body}</Typography>
											<br />
											<span className={classes.alertTime}>
												{alert.timestamp ? format(new Date(alert.timestamp), "HH:mm") : ""}
											</span>
										</>
									}
								/>
							</ListItem>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default NotificationsPopOver;
